import { useState, FormEvent } from 'react';

const PricingSection = () => {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const [showSignupModal, setShowSignupModal] = useState(false);

  return (
    <>
      <section id="pricing" className="py-20 bg-gradient-to-br from-gray-50 to-pink-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Simple, Transparent Pricing
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Get a complete client-handling system for less than the cost of one employee
            </p>
            
            {/* Billing Toggle */}
            <div className="inline-flex items-center bg-white rounded-full p-1 shadow-md">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`whitespace-nowrap px-6 py-2 rounded-full font-semibold transition-all duration-300 cursor-pointer ${
                  billingCycle === 'monthly'
                    ? 'bg-pink-500 text-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('annual')}
                className={`whitespace-nowrap px-6 py-2 rounded-full font-semibold transition-all duration-300 cursor-pointer ${
                  billingCycle === 'annual'
                    ? 'bg-pink-500 text-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Annual <span className="text-xs ml-1">(Save $1,065)</span>
              </button>
            </div>
          </div>
          
          {/* Pricing Card */}
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border-4 border-pink-500">
              <div className="bg-gradient-to-r from-pink-500 to-purple-500 px-8 py-6 text-center">
                <h3 className="text-3xl font-bold text-white mb-2">The ReceptionGenie Bundle</h3>
                <p className="text-pink-100">Your Complete Client-Handling Solution</p>
              </div>
              
              <div className="p-8">
                <div className="text-center mb-8">
                  <div className="text-5xl font-bold text-gray-900 mb-2">
                    {billingCycle === 'monthly' ? '$297' : '$2,499'}
                    <span className="text-2xl text-gray-500 font-normal">
                      /{billingCycle === 'monthly' ? 'month' : 'year'}
                    </span>
                  </div>
                  {billingCycle === 'annual' && (
                    <div className="text-green-600 font-semibold">
                      Save $1,065 per year!
                    </div>
                  )}
                </div>
                
                {/* Features List */}
                <div className="space-y-4 mb-8">
                  {[
                    { title: '24/7 Virtual Receptionist', value: '$999/mo value' },
                    { title: 'Automated Review Management', value: '$299/mo value' },
                    { title: 'Conversational AI Texting', value: '$499/mo value' },
                    { title: 'Onboarding & Setup Call', value: '$297 value' },
                    { title: 'VIP 24/7 Customer Support', value: '$599/mo value' },
                    { title: 'Call Recordings & Transcriptions', value: '$99/mo value' },
                    { title: 'Real-Time Notifications', value: 'Priceless' }
                  ].map((feature, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <i className="ri-check-line text-green-600 text-sm"></i>
                        </div>
                        <span className="font-semibold text-gray-900">{feature.title}</span>
                      </div>
                      <span className="text-sm text-gray-500 whitespace-nowrap ml-4">{feature.value}</span>
                    </div>
                  ))}
                </div>
                
                <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-2xl p-6 mb-8 border-2 border-yellow-200">
                  <div className="text-center">
                    <div className="text-sm font-semibold text-gray-600 mb-2">Total Value</div>
                    <div className="text-4xl font-bold text-gray-900 mb-2">$4,000+/month</div>
                    <div className="text-lg text-gray-700">
                      You pay only <span className="font-bold text-pink-600">${billingCycle === 'monthly' ? '297' : '208'}/month</span>
                    </div>
                  </div>
                </div>
                
                {billingCycle === 'monthly' ? (
                  <a
                    href="https://buy.stripe.com/6oU9AV5QPa9V4Y88ZYg3601"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="whitespace-nowrap w-full px-8 py-5 bg-pink-500 hover:bg-pink-600 text-white text-xl font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer block text-center"
                  >
                    Select Monthly Plan
                  </a>
                ) : (
                  <a
                    href="https://buy.stripe.com/cNi5kF1Az4PB8akfomg3600"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="whitespace-nowrap w-full px-8 py-5 bg-pink-500 hover:bg-pink-600 text-white text-xl font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer block text-center"
                  >
                    Select Annual Plan
                  </a>
                )}
                
                <div className="text-center mt-6 text-sm text-gray-500">
                  No credit card required • Cancel anytime • 30-day money-back guarantee
                </div>
              </div>
            </div>
          </div>
          
          {/* Comparison Section */}
          <div className="mt-20">
            <h3 className="text-3xl font-bold text-gray-900 text-center mb-12">
              Compare Your Options
            </h3>
            
            <div className="grid md:grid-cols-3 gap-6">
              {/* Traditional Voicemail */}
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <h4 className="text-xl font-bold text-gray-900 mb-4 text-center">Traditional Voicemail</h4>
                <div className="space-y-3">
                  <div className="flex items-start gap-2">
                    <i className="ri-close-circle-fill text-red-500 text-xl flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-700">Missed calls and lost leads</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <i className="ri-close-circle-fill text-red-500 text-xl flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-700">Clients hang up or move on</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <i className="ri-close-circle-fill text-red-500 text-xl flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-700">No scheduling capabilities</span>
                  </div>
                </div>
              </div>
              
              {/* Answering Service */}
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <h4 className="text-xl font-bold text-gray-900 mb-4 text-center">Answering Service</h4>
                <div className="space-y-3">
                  <div className="flex items-start gap-2">
                    <i className="ri-close-circle-fill text-orange-500 text-xl flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-700">Expensive, inconsistent</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <i className="ri-close-circle-fill text-orange-500 text-xl flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-700">Needs training &amp; oversight</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <i className="ri-close-circle-fill text-orange-500 text-xl flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-700">Limited availability</span>
                  </div>
                </div>
              </div>
              
              {/* ReceptionGenie */}
              <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-2xl p-6 shadow-lg border-2 border-pink-500">
                <h4 className="text-xl font-bold text-gray-900 mb-4 text-center">ReceptionGenie</h4>
                <div className="space-y-3">
                  <div className="flex items-start gap-2">
                    <i className="ri-check-circle-fill text-green-500 text-xl flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-700">Affordable, 24/7 coverage</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <i className="ri-check-circle-fill text-green-500 text-xl flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-700">Handles everything autonomously</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <i className="ri-check-circle-fill text-green-500 text-xl flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-700">Immediate response &amp; booking</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {showSignupModal && <SignupModal onClose={() => setShowSignupModal(false)} />}
    </>
  );
};

interface SignupModalProps {
  onClose: () => void;
}

const SignupModal = ({ onClose }: SignupModalProps) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    plan: 'monthly'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d4ii6dqjg2jl50j1sdj0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', company: '', plan: 'monthly' });
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-r from-pink-500 to-purple-500 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <h3 className="text-2xl font-bold text-white">Start Your Free Trial</h3>
          <button 
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-white hover:text-pink-100 cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4" data-readdy-form>
          <div>
            <label htmlFor="signup-name" className="block text-sm font-semibold text-gray-700 mb-2">
              Full Name *
            </label>
            <input
              type="text"
              id="signup-name"
              name="name"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm"
              placeholder="John Doe"
            />
          </div>
          
          <div>
            <label htmlFor="signup-email" className="block text-sm font-semibold text-gray-700 mb-2">
              Email Address *
            </label>
            <input
              type="email"
              id="signup-email"
              name="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm"
              placeholder="john@company.com"
            />
          </div>
          
          <div>
            <label htmlFor="signup-phone" className="block text-sm font-semibold text-gray-700 mb-2">
              Phone Number *
            </label>
            <input
              type="tel"
              id="signup-phone"
              name="phone"
              required
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm"
              placeholder="(555) 123-4567"
            />
          </div>
          
          <div>
            <label htmlFor="signup-company" className="block text-sm font-semibold text-gray-700 mb-2">
              Company Name
            </label>
            <input
              type="text"
              id="signup-company"
              name="company"
              value={formData.company}
              onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm"
              placeholder="Your Company"
            />
          </div>
          
          <div>
            <label htmlFor="signup-plan" className="block text-sm font-semibold text-gray-700 mb-2">
              Select Plan *
            </label>
            <select
              id="signup-plan"
              name="plan"
              required
              value={formData.plan}
              onChange={(e) => setFormData({ ...formData, plan: e.target.value })}
              className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm cursor-pointer"
            >
              <option value="monthly">Monthly - $297/month</option>
              <option value="annual">Annual - $2,499/year (Save $1,065)</option>
            </select>
          </div>
          
          {submitStatus === 'success' && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
              Success! We'll contact you shortly to set up your account.
            </div>
          )}
          
          {submitStatus === 'error' && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              Something went wrong. Please try again.
            </div>
          )}
          
          <button
            type="submit"
            disabled={isSubmitting}
            className="whitespace-nowrap w-full px-6 py-4 bg-pink-500 hover:bg-pink-600 disabled:bg-gray-400 text-white font-semibold rounded-lg transition-all duration-300 cursor-pointer"
          >
            {isSubmitting ? 'Processing...' : 'Start Free Trial'}
          </button>
          
          <div className="text-center text-xs text-gray-500">
            No credit card required • Cancel anytime
          </div>
        </form>
      </div>
    </div>
  );
};

export default PricingSection;